import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { InView } from 'react-intersection-observer'
import canUseDom from '@boltui/utils/src/canUseDom'

const ImageSrcFormat = (src, width, height, quality, templates, fallbackFmt) => {
  const supportedResolutions = [1, 2]
  let srcSet = ''
  let srcSetJpeg = ''
  const templatesParam = templates.length ? `${templates.join('&')}&` : ``
  supportedResolutions.forEach((res) => {
    const widthParam = typeof width !== 'undefined' ? `w=${width * res}&` : ``
    const heightParam = typeof height !== 'undefined' ? `h=${height * res}&` : ``
    srcSet += `${src}?${templatesParam}${widthParam}${heightParam}qlt=${quality}&fmt=webp ${res}x, `
    srcSetJpeg += `${src}?${templatesParam}${widthParam}${heightParam}qlt=${quality}&fmt.jpeg.interlaced=true ${res}x, `
  })
  const widthParam = typeof width !== 'undefined' ? `w=${width}&` : ``
  const heightParam = typeof height !== 'undefined' ? `h=${height}&` : ``
  return {
    src: `${src}?${templatesParam}${widthParam}${heightParam}qlt=${quality}&${fallbackFmt}`,
    srcSet,
    srcSetJpeg
  }
}

const LazyWrapper = ({ lazy, nativeLazy, lazyThreshold, lazyWrapElement, children }) => {
  const [isInView, toggleInView] = useState(false)
  if (!lazy) return children

  if (!nativeLazy || (nativeLazy && canUseDom() && !('loading' in HTMLImageElement.prototype))) {
    return (
      <InView
        as={lazyWrapElement}
        rootMargin={'200px 0px'}
        threshold={lazyThreshold}
        onChange={(inView) => toggleInView(inView)}
        triggerOnce>
        {isInView && children}
      </InView>
    )
  }
  return children
}

const Image = ({
  src,
  alt,
  width,
  height,
  quality,
  templates,
  responsive,
  lazy,
  nativeLazy,
  lazyThreshold,
  lazyWrapElement,
  className,
  fallbackFmt,
  'data-test': dataTest,
  ...props
}) => {
  const { src: imageSrc, srcSet, srcSetJpeg } = ImageSrcFormat(src, width, height, quality, templates, fallbackFmt)
  return (
    <LazyWrapper lazy={lazy} nativeLazy={nativeLazy} lazyThreshold={lazyThreshold} lazyWrapElement={lazyWrapElement}>
      <picture>
        <source type='image/webp' srcSet={srcSet} />
        <source type='image/pjpeg' srcSet={srcSetJpeg} />
        <img
          src={imageSrc}
          width={responsive ? '100%' : width}
          height={responsive ? '100%' : height}
          alt={alt}
          data-test={dataTest}
          className={className}
          loading={lazy && nativeLazy ? 'lazy' : 'auto'}
          {...props}
        />
      </picture>
    </LazyWrapper>
  )
}

Image.propTypes = {
  /**
   * Required prop for the Image src
   */
  src: PropTypes.string.isRequired,

  /**
   * Required prop for the Image alt text
   */
  alt: PropTypes.string.isRequired,

  /**
   * Prop for the Image width
   */
  width: PropTypes.number,

  /**
   * Prop for the Image height
   */
  height: PropTypes.number,

  /**
   * Optional prop for the Image quality
   */
  quality: PropTypes.number,

  /**
   * Optional prop for the fallback format of an image (not webp)
   */
  fallbackFmt: PropTypes.string,

  /**
   * Specify an optional Amplience templates array to be added to the request as POST param
   */
  templates: PropTypes.array,

  /**
   * Specify whether the Image should only load when in viewport
   */
  lazy: PropTypes.bool,

  /**
   * Specify whether the Image should load using browser native lazy load
   */
  nativeLazy: PropTypes.bool,

  /**
   * Number between 0 and 1 indicating the percentage that should be visible before triggering. Can also be an array of numbers, to create multiple trigger points.
   */
  lazyThreshold: PropTypes.number,

  /**
   * The wrapping element for the lazy loaded image
   */
  lazyWrapElement: PropTypes.string,

  /**
   * Specify whether the Image should have 100% height and width
   */
  responsive: PropTypes.bool,

  /**
   * Specify an optional className to be added to your Image
   */
  className: PropTypes.string,

  /**
   * Specify an optional data-test attribute to be added to your Image for e2e testing
   */
  'data-test': PropTypes.string
}

Image.defaultProps = {
  quality: 75,
  templates: [],
  lazy: false,
  nativeLazy: false,
  lazyThreshold: 0.5,
  lazyWrapElement: 'div',
  responsive: false,
  fallbackFmt: 'fmt.jpeg.interlaced=true',
  'data-test': 'component-image'
}

export default Image
